<template>
  <div class="site__links">
    <component
      v-for="(link, index) in links"
      :key="link._id.$oid"
      :is="getLinkType(link.type)"
      :link="link"
      v-bind="getLinkProps(link.type)"
      @click="handleClick(link, index, $event)"
      @event="handleEvent(link, $event)"
      ref="linksRef"
    />
  </div>
</template>

<script>
import {
  ProfileLink,
  ProfileCarousel,
  ProfileDivider,
  ProfileTitle,
  ProfileFeed,
  ProfileEmbed,
  ProfileRequest,
  ProfileSupport,
  ProfileImageGrid,
  EVENTS,
} from '@campsite-bio/component-lib';
import {
  computed,
  onMounted,
  provide,
  ref,
  toRefs,
  useContext,
} from '@nuxtjs/composition-api';
import { useWindowSize } from '@vueuse/core';
import debounce from 'lodash/debounce';

import OptInFormWrapper from '@/components/links/opt-in-form-wrapper';
import FormWrapper from '@/components/links/form-wrapper';
import { getMeta } from '~/utils/meta';

export default {
  components: {
    ProfileLink,
    ProfileCarousel,
    ProfileDivider,
    OptInFormWrapper,
    ProfileTitle,
    ProfileFeed,
    ProfileEmbed,
    ProfileRequest,
    ProfileSupport,
    ProfileImageGrid,
    FormWrapper,
  },

  props: {
    isFreePlan: Boolean,
  },

  setup(props) {
    const { isFreePlan } = toRefs(props);
    const { store, $trackEvent } = useContext();
    const linksRef = ref([]);
    const allEventData = ref([]);
    const profile = computed(() => store.state.campsite);

    provide('profile', profile);

    const campsite = computed(() => store.state.campsite);
    const { height: windowHeight } = useWindowSize();

    function getLinkProps(type) {
      const settings = campsite.value.theme
        ? campsite.value.theme
        : campsite.value;

      const defaultProps = {
        enableUtmParameters: getMeta(
          'enable_utm_parameters',
          false,
          campsite.value.meta,
        ),
        utmMedium: getMeta('utm_medium', null, campsite.value.meta),
        utmSource: getMeta('utm_source', null, campsite.value.meta),
        imageStyle: campsite.value.site_button_image_style,
        btnSize: campsite.value.site_button_size,
      };

      if (settings.site_button_legacy) {
        defaultProps.imageSize = campsite.value.site_button_image_size;
      }

      switch (type) {
        case 'embed':
          return {
            ...defaultProps,
            trackVideoPlayEvents: !isFreePlan.value,
          };
        case 'delimiter':
          return {
            ...defaultProps,
            variant: settings.site_divider_style,
          };
        case 'image-grid':
          return {
            ...defaultProps,
            disableMultipleUrls: isFreePlan.value,
          };

        default:
          return { ...defaultProps, variant: settings.site_button_style };
      }
    }

    // Track events from links
    function handleEvent(link, e) {
      let eventName = e;
      let eventData = {};
      // Event can come in as an object or a string
      if (typeof e === 'object') {
        const { name, ...rest } = e;
        eventName = name;
        eventData = rest;
      }
      // Don't track impressions for free plans
      if (isFreePlan.value && eventName === EVENTS.IMPRESSION) return;

      // Capture the distance the link was from the top of the page when the event was fired
      if (eventName === EVENTS.IMPRESSION) {
        const currentLink = linksRef.value.find((l) => l.id === link._id.$oid);
        const { top } = currentLink.$el.getBoundingClientRect();
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        // Take the distance from the top of the link and divide it by the height of the window
        // < 1 it was in view on page load, > 1 it was out of view
        eventData.from_top = parseFloat(
          ((top + scrollTop) / windowHeight.value).toFixed(2),
        );
      }

      allEventData.value.push({
        ...eventData,
        link_id: link._id.$oid,
        epoch: Date.now(),
        event_name: eventName,
      });
      sendEventsDebounced();
    }

    function sendEvents() {
      if (allEventData.value.length === 0) return;

      $trackEvent(allEventData.value, {
        campsite_id: campsite.value._id.$oid,
      });
      // Clear events so they don't get sent again
      allEventData.value.length = 0;
    }
    const sendEventsDebounced = debounce(sendEvents, 3000);

    onMounted(() => {
      // Make sure straggler events are sent, even if the page is unloaded
      window.addEventListener('beforeunload', () => sendEvents());
    });

    return {
      getLinkProps,
      handleEvent,
      linksRef,
    };
  },

  computed: {
    campsite() {
      return this.$store.state.campsite;
    },

    links() {
      return this.$store.state.links;
    },
  },

  methods: {
    getLinkType(type) {
      switch (type) {
        case 'opt-in-form':
          return 'OptInFormWrapper';
        case 'title':
          return 'ProfileTitle';
        case 'carousel':
          return 'ProfileCarousel';
        case 'delimiter':
          return 'ProfileDivider';
        case 'feed':
          return 'ProfileFeed';
        case 'embed':
          return 'ProfileEmbed';
        case 'form':
          return 'FormWrapper';
        case 'request':
          return 'ProfileRequest';
        case 'support':
          return 'ProfileSupport';
        case 'image-grid':
          return 'ProfileImageGrid';

        default:
          return 'ProfileLink';
      }
    },

    getLinkText(link) {
      switch (link.type) {
        case 'form':
          return link.form_options.button_text;
        case 'feed':
          return null;
        case 'image-grid':
          return null;
        default:
          return link.label;
      }
    },

    handleClick(link, index, e = {}) {
      let data = { href: link.url, text: this.getLinkText(link), ...e };
      const eventTag = link.meta
        ? link.meta.find((m) => m.name === 'event-tag').value
        : null;

      if (link.type) data.type = link.type;

      this.$track(
        {
          campsite_id: this.campsite._id.$oid,
          link_id: link._id.$oid,
          ...data,
        },
        data,
      );

      if (eventTag) {
        this.handleEvent(link, eventTag);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.site__links {
  position: relative;
  margin-top: 20px;
}
</style>
